import { useEffect, useState, useCallback } from "react";
import { CalendarDays, Wallet, FileWarning, UserPlus, AlertTriangle, CheckCircle2, Circle, X } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money, todayStr } from "../lib/helpers";
import { PageHeader, StatCard, StatusBadge, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import AppointmentDrawer from "../components/AppointmentDrawer";
import AppointmentForm from "../components/AppointmentForm";

function GettingStarted() {
  const { services, staff, settings, user } = useApp();
  const [hidden, setHidden] = useState(localStorage.getItem(`mcm_gs_${user.clinic_id}`) === "1");
  if (user.role !== "owner" || hidden) return null;
  const steps = [
    ["Add your treatments and prices", "/app/services", services.length > 0],
    ["Add your team (doctors, reception)", "/app/staff", staff.length > 1],
    ["Upload your logo and colours", "/app/settings", !!settings.branding?.logo],
    ["Put the booking button on your website & Google", "/app/settings?tab=website", false],
  ];
  const hide = () => { localStorage.setItem(`mcm_gs_${user.clinic_id}`, "1"); setHidden(true); };
  return (
    <div className="relative rounded-[var(--radius)] border bg-card p-6 mb-8 fade-up" data-testid="getting-started">
      <button aria-label="Dismiss" className="absolute right-4 top-4 text-muted-foreground" onClick={hide} data-testid="getting-started-hide"><X className="h-4 w-4" /></button>
      <h2 className="text-lg font-semibold mb-4">Get started in 4 steps</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map(([label, to, done], i) => (
          <Link key={label} to={to} data-testid={`getting-started-step-${i}`} className="lift flex items-start gap-3 rounded-md border p-4 text-sm">
            {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
            <span className={done ? "line-through text-muted-foreground" : "font-medium"}>{label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { locationId, user, can } = useApp();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard", { params: { date: todayStr(), location_id: locationId || undefined } }).then((r) => setData(r.data));
  }, [locationId]);
  useEffect(() => { load(); }, [load]);

  const revenue = data ? Object.entries(data.revenue || {}) : [];
  return (
    <div>
      <PageHeader eyebrow={format(new Date(), "EEEE d MMMM yyyy")} title={`${t("today_in_clinic")}, ${user.name.split(" ")[0]}`}>
        {can("agenda.write") && (
          <Button data-testid="dashboard-new-appointment-btn" className="rounded-full" onClick={() => setCreating(true)}>{t("new_appointment")}</Button>
        )}
      </PageHeader>
      <GettingStarted />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10 stagger">
        <StatCard testId="stat-appointments" label={t("appointments")} icon={CalendarDays} value={data?.appointments.length ?? "–"} />
        <StatCard testId="stat-revenue-today" label={t("revenue_today")} icon={Wallet}
          value={revenue.length ? <div className="space-y-1">{revenue.map(([c, v]) => <div key={c} className="text-xl sm:text-2xl">{money(v.total, c, lang)}</div>)}</div> : "–"} />
        <StatCard testId="stat-pending-consents" label={t("pending_consents")} icon={FileWarning} value={data?.pending_consents ?? "–"}
          tone={data?.pending_consents ? "border-amber-500/40 bg-amber-500/5" : ""} />
        <StatCard testId="stat-new-clients" label={t("new_clients_week")} icon={UserPlus} value={data?.new_clients_week ?? "–"} />
      </div>
      <div className="rounded-[var(--radius)] border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">{t("appointments")}</h2>
        </div>
        <div className="divide-y" data-testid="dashboard-appointments-list">
          {data && !data.appointments.length && <div className="p-6"><Empty text={t("no_appointments")} /></div>}
          {data?.appointments.map((a) => (
            <button key={a.id} data-testid={`dashboard-appt-${a.id}`} onClick={() => setOpenId(a.id)}
              className="w-full text-left flex flex-wrap items-center gap-4 px-6 py-4 hover:bg-muted/50 transition-colors">
              <span className="font-mono text-sm w-24 shrink-0">{a.start_time}–{a.end_time}</span>
              <span className="h-8 w-1 rounded-full" style={{ background: a.service_color || "hsl(var(--primary))" }} />
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold">{a.client_name}</div>
                <div className="text-sm text-muted-foreground">{a.service_name} · {a.professional_name} · {a.location_name}</div>
              </div>
              {a.consent_missing && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-500/15 rounded-full px-2.5 py-1">
                  <AlertTriangle className="h-3 w-3" />{t("consent_missing")}
                </span>
              )}
              <StatusBadge status={a.status} />
            </button>
          ))}
        </div>
      </div>
      <AppointmentDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <AppointmentForm open={creating} onOpenChange={setCreating} initial={{ date: todayStr() }} onSaved={load} />
    </div>
  );
}
